import { gql, useLazyQuery, useMutation, useQuery, useReactiveVar } from '@apollo/client';
import { Typography } from '@mui/material';
import Button from '@mui/material/Button';

import TextField from '@mui/material/TextField';
import { type FormikHelpers, useFormik } from 'formik';
import { jsonToGraphQLQuery } from 'json-to-graphql-query';
import React, { type MouseEvent, useCallback, useEffect, useRef, useState } from 'react';
import { assetMarkers, createMarker, deleteMarker, updateMarker } from 'src/api/markers';
import DownloadMarkerModal from 'src/components/Modals/DownloadMarkerModal';
import MarkersList from 'src/components/Tabs/MarkersTab/MarkersList/MarkersList';
import styles from 'src/components/Tabs/MarkersTab/MarkersTabStyles';
import { API_PATH, INSERT_MODE, VISUAL_MODE } from 'src/constants';
import { assetTimecodeMeta, markerTimecodeMeta, timeCodeFromSecondsAssetIn } from 'src/dynamoQueries/timecodes';
import { isVisualMode, setGlobalStateMode } from 'src/helpers/mode';
import { isAwsS3Url } from 'src/helpers/url';
import viewStateVar from 'src/store/viewState';
import * as Yup from 'yup';

interface MarkerNode {
  id: string;
  markIn?: string;
  markOut?: string;
  title?: string;
  description?: string;
}

interface MarkerEdge {
  node: MarkerNode;
}

interface MarkersListData {
  allMarkers: {
    edges: MarkerEdge[];
  };
}

interface AssetTimecodeMetadata {
  assetTimecodeMetadata: {
    dropframe: boolean;
  };
}

interface MarkerTimecodeMetadata {
  markerTimecodeMetadata: {
    inTimecodeSeconds?: number;
    outTimecodeSeconds?: number;
    timecodeOffsetSeconds?: number;
  };
}

interface TimecodeFromSecondsData {
  timecodeFromSecondsAssetIn: {
    timecode: string;
  };
}

interface FormValues {
  markIn: string;
  markOut: string;
  title: string;
  description: string;
  markInSeconds: number | undefined;
  markOutSeconds: number | undefined;
}

interface VideoJsPlayer {
  currentTime: ((seconds?: number) => number) | number;
  currentTimecode?: (inSeconds?: number, outSeconds?: number, title?: string) => void;
  markers?: {
    removeAll: () => void;
  };
  el?: () => HTMLElement;
  getAttribute?: (name: string) => string;
}

interface PlayerRef {
  current?: VideoJsPlayer;
}

type PlayerType = VideoJsPlayer | PlayerRef | HTMLVideoElement;

interface TabPaneProps {
  player: PlayerType & { getAttribute: (name: string) => string };
  clipId: string;
  setMarker: (marker: MarkerNode | Record<string, never>) => void;
}

const MarkersTab: React.FC<TabPaneProps> = ({ player, clipId, setMarker }) => {
  const classes = styles();
  const viewState = useReactiveVar(viewStateVar);

  const [createMarkerRequest] = useMutation(createMarker);
  const [updateMarkerRequest] = useMutation(updateMarker);
  const [deleteMarkerRequest] = useMutation(deleteMarker);

  const [editMode, setEditMode] = useState(false);
  const [selectedMarker, setSelectedMarker] = useState<string | undefined>();
  const [targetEndpoint, setTargetEndpoint] = useState<'in' | 'out'>('in');
  const [isAwsMedia, setIsAwsMedia] = useState(false);
  const markerId = useRef<string>('');
  const [markInDirty, setMarkInDirty] = useState(false);
  const [markOutDirty, setMarkOutDirty] = useState(false);

  // biome-ignore lint/correctness/useExhaustiveDependencies: intentionally runs only on mount to determine AWS media status
  useEffect(() => {
    const playerSrc = player.getAttribute('src');
    if (!playerSrc) return;
    const mediaId = playerSrc.split('/').pop();

    fetch(`${window.location.origin}${API_PATH}/media/${mediaId}`)
      .then((response) => {
        const isS3 = isAwsS3Url(response.url);

        if (isS3) {
          setIsAwsMedia(true);
        }
      })
      .catch((error) => {
        console.error('Fetch error:', error);
      });
  }, []);

  const { data: markersList } = useQuery<MarkersListData>(assetMarkers, {
    variables: { asset_Id: clipId },
    fetchPolicy: 'cache-and-network',
  });

  const assetTimecodeQuery = () => {
    const atm = assetTimecodeMeta(false);
    atm.query.assetTimecodeMetadata.duration = false;
    atm.query.assetTimecodeMetadata.durationSeconds = false;
    atm.query.assetTimecodeMetadata.framerate = false;
    atm.query.assetTimecodeMetadata.frameDelimiter = false;
    atm.query.assetTimecodeMetadata.timecodeOffset = false;
    atm.query.assetTimecodeMetadata.timecodeOffsetSeconds = false;
    const gQuery = jsonToGraphQLQuery(atm, { pretty: true });
    return gql(gQuery);
  };

  const timeCodeFromSecondsBuildQuery = () => {
    const tcsQuery = timeCodeFromSecondsAssetIn(true);
    const gQuery = jsonToGraphQLQuery(tcsQuery, { pretty: true });
    return gql(gQuery);
  };

  const markerTimecodeQuery = () => {
    const mtm = markerTimecodeMeta(true);
    const gQuery = jsonToGraphQLQuery(mtm, { pretty: true });
    return gql(gQuery);
  };

  const { data: assetTimecodeMetadata } = useQuery<AssetTimecodeMetadata>(assetTimecodeQuery(), {
    variables: { assetId: clipId },
  });

  const [getMarkerTimecodeMeta, markerTcMeta] = useLazyQuery<MarkerTimecodeMetadata>(markerTimecodeQuery(), {
    fetchPolicy: 'no-cache',
  });

  const [getTimecodeFromSeconds] = useLazyQuery<TimecodeFromSecondsData>(timeCodeFromSecondsBuildQuery(), {
    fetchPolicy: 'no-cache',
  });

  const getCurrentSeconds = (): number | undefined => {
    try {
      const p = player as PlayerRef;
      if (p && typeof p.current === 'object' && p.current && typeof p.current.currentTime === 'function') {
        return p.current.currentTime();
      }
      const vp = player as VideoJsPlayer;
      if (vp && typeof vp.currentTime === 'function') {
        return vp.currentTime();
      }
      if (vp && typeof vp.currentTime === 'number') {
        return vp.currentTime;
      }
    } catch (_) {
      // Ignore errors reading current time
    }
    return undefined;
  };

  const clearPlayerMarkers = (): void => {
    try {
      const p = player as PlayerRef;
      const playerInstance = p && typeof p.current === 'object' ? p.current : (player as VideoJsPlayer);
      if (
        playerInstance &&
        typeof playerInstance.markers === 'object' &&
        typeof playerInstance.markers?.removeAll === 'function'
      ) {
        playerInstance.markers.removeAll();
      }
      const el = playerInstance && typeof playerInstance.el === 'function' ? playerInstance.el() : null;
      if (el) {
        el.querySelectorAll('.vjs-marker, .akomi-range, .akomi-point').forEach((n) => {
          if (n?.parentNode) n.parentNode.removeChild(n);
        });
      }
    } catch (e) {
      console.log(e);
    }
  };

  const formik = useFormik<FormValues>({
    initialValues: {
      markIn: '',
      markOut: '',
      title: '',
      description: '',
      markInSeconds: undefined,
      markOutSeconds: undefined,
    },
    validationSchema: Yup.object({
      markIn: Yup.string().required('This field is required'),
      markOut: Yup.string(),
      title: Yup.string().nullable(),
      description: Yup.string().nullable(),
    }),
    onSubmit: async (
      { markIn, markOut, title, description, markInSeconds, markOutSeconds }: FormValues,
      _helpers: FormikHelpers<FormValues>,
    ) => {
      const payloadMarkInSeconds = markInDirty ? undefined : markInSeconds;
      const payloadMarkOutSeconds = markOutDirty ? undefined : markOutSeconds;
      const variables = {
        title,
        description,
        markIn,
        markOut: markOut === '' ? undefined : markOut,
        markInSeconds: typeof payloadMarkInSeconds === 'number' ? payloadMarkInSeconds : undefined,
        markOutSeconds: typeof payloadMarkOutSeconds === 'number' ? payloadMarkOutSeconds : undefined,
      };

      if (editMode) {
        updateMarkerRequest({
          variables: {
            ...variables,
            markerId: markerId.current,
          },
          refetchQueries: [assetMarkers],
        }).then(() => {
          cancelEditMode();
        });
      } else {
        createMarkerRequest({
          variables: { ...variables, assetId: clipId },
          refetchQueries: [assetMarkers],
        }).then(() => {
          cancelEditMode();
        });
      }
    },
  });

  const downloadMarkerClickHandler = (item: MarkerNode): void => {
    setSelectedMarker(item.id);
    markerId.current = item.id;
    viewStateVar({
      ...viewStateVar(),
      downloadMarkerDialog: {
        markerId: markerId.current,
        assetId: clipId,
      },
    });
  };

  const deleteMarkerClickHandler = (item: MarkerNode): void => {
    markerId.current = item.id;
    deleteMarkerRequest({
      variables: { markerId: markerId.current },
      refetchQueries: [assetMarkers],
    }).then(() => {
      clearPlayerMarkers();
      setMarker?.({});
      cancelEditMode();
    });
  };

  const buttonDeleteMarker = (): void => {
    deleteMarkerRequest({
      variables: { markerId: markerId.current },
      refetchQueries: [assetMarkers],
    }).then(() => {
      clearPlayerMarkers();
      setMarker?.({});
      cancelEditMode();
    });
  };

  const onClickItem = (item: MarkerNode, event: MouseEvent<HTMLTableRowElement>): void => {
    setSelectedMarker(item.id);
    markerId.current = item.id;

    getMarkerTimecodeMeta({ variables: { markerId: item.id } });

    const wantsOut = !!event?.shiftKey && !!item.markOut;
    setTargetEndpoint(wantsOut ? 'out' : 'in');

    if (markerId.current && markerId.current !== '' && markerId.current !== item.id) {
      cancelEditMode();
    }

    setMarker(item);

    setEditMode(true);
    formik.setFieldValue('markIn', item.markIn || '');
    formik.setFieldValue('markOut', item.markOut || '');
    formik.setFieldValue('title', item.title || '');
    formik.setFieldValue('description', item.description || '');
  };

  const getMarker = (markerId: string | undefined): MarkerEdge | undefined => {
    return markersList?.allMarkers?.edges?.find((item) => item.node.id === markerId);
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: player, selectedMarker, getMarker, and markersList are intentionally excluded to avoid unnecessary re-renders
  useEffect(() => {
    const marker = getMarker(selectedMarker);

    if (markerTcMeta?.data && player) {
      const meta = markerTcMeta.data.markerTimecodeMetadata;
      const inS = typeof meta?.inTimecodeSeconds === 'number' ? Math.max(0, meta.inTimecodeSeconds) : undefined;
      const outS = typeof meta?.outTimecodeSeconds === 'number' ? Math.max(0, meta.outTimecodeSeconds) : undefined;
      const seekSeconds = targetEndpoint === 'out' && typeof outS === 'number' ? outS : inS;

      const p = player as PlayerRef;
      if (
        p &&
        typeof p.current === 'object' &&
        p.current &&
        typeof p.current.currentTime === 'function' &&
        typeof seekSeconds === 'number'
      ) {
        p.current.currentTime(seekSeconds);
      } else {
        const vp = player as VideoJsPlayer;
        if (typeof vp.currentTimecode === 'function') {
          vp.currentTimecode(seekSeconds, outS, marker?.node?.title);
        } else if (typeof vp.currentTime === 'function' && typeof seekSeconds === 'number') {
          vp.currentTime(seekSeconds);
        } else if (typeof seekSeconds === 'number' && 'currentTime' in player) {
          try {
            (player as HTMLVideoElement).currentTime = seekSeconds;
          } catch (_e) {
            // ignore
          }
        }
      }
    }
  }, [markerTcMeta.data, targetEndpoint]);

  const editMarkerClickHandler = (item: MarkerNode): void => {
    getMarkerTimecodeMeta({ variables: { markerId: item.id } });

    setSelectedMarker(item.id);
    setEditMode(true);
    markerId.current = item.id;
    formik.setFieldValue('markIn', item.markIn || '');
    formik.setFieldValue('markOut', item.markOut || '');
    formik.setFieldValue('title', item.title || '');
    formik.setFieldValue('description', item.description || '');
  };

  const cancelEditMode = (): void => {
    setEditMode(false);
    markerId.current = '';
    formik.resetForm();
    clearPlayerMarkers();
    setMarker?.({});
  };

  const onCancelClick = (): void => {
    setSelectedMarker(undefined);
    cancelEditMode();
  };

  const fetchTimecode = (fieldName: 'markIn' | 'markOut') => (): void => {
    const currentSeconds = getCurrentSeconds();
    if (typeof currentSeconds === 'number') {
      if (fieldName === 'markIn') {
        formik.setFieldValue('markInSeconds', currentSeconds);
        setMarkInDirty(false);
      }
      if (fieldName === 'markOut') {
        formik.setFieldValue('markOutSeconds', currentSeconds);
        setMarkOutDirty(false);
      }
    }

    getTimecodeFromSeconds({
      variables: { assetId: clipId, seconds: currentSeconds },
      onCompleted: (data) => {
        const { timecodeFromSecondsAssetIn: tc } = data;
        formik.setFieldValue(fieldName, tc.timecode);
      },
    });
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: fetchTimecode is stable and intentionally excluded to avoid re-creating callback
  const onKeyUp = useCallback(
    (e: KeyboardEvent): void => {
      if (e.keyCode === 73) {
        // PRESS i
        fetchTimecode('markIn')();
      }
      if (e.keyCode === 79) {
        // PRESS o
        fetchTimecode('markOut')();
      } else if (e.keyCode === 77) {
        // PRESS m
        formik.handleSubmit();
      }
    },
    [formik, clipId],
  );

  useEffect(() => {
    if (isVisualMode(viewState.mode)) window.addEventListener('keyup', onKeyUp);
    if (!isVisualMode(viewState.mode)) window.removeEventListener('keyup', onKeyUp);
    return () => window.removeEventListener('keyup', onKeyUp);
  }, [viewState.mode, onKeyUp]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: formik.resetForm is stable, intentionally reset form only when clipId changes
  useEffect(() => {
    formik.resetForm();
  }, [clipId]);

  const onlyNumbersOnChange = (name: 'markIn' | 'markOut', e: React.ChangeEvent<HTMLInputElement>): void => {
    const { value } = e.target;
    if (value.match(/^[0-9:;]*$/)) {
      formik.setFieldValue(name, value);
      if (name === 'markIn') {
        setMarkInDirty(true);
        formik.setFieldValue('markInSeconds', undefined);
      }
      if (name === 'markOut') {
        setMarkOutDirty(true);
        formik.setFieldValue('markOutSeconds', undefined);
      }
    }
  };

  return (
    <React.Fragment>
      <form onSubmit={formik.handleSubmit}>
        <div className={classes.markersTop}>
          <div className={classes.pressFields}>
            <TextField
              value={formik.values.markIn}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => onlyNumbersOnChange('markIn', e)}
              className={classes.textFields}
              fullWidth
              id="cyp-markers-in-input-field"
              label="(Press i)"
              variant="outlined"
            />
            <Button
              id="cyp-markers-in-input-btn"
              onClick={fetchTimecode('markIn')}
              classes={{ root: classes.inAndOutButton }}
              variant="contained"
              color="secondary"
            >
              <Typography>IN</Typography>
            </Button>
            <TextField
              className={classes.textFields}
              fullWidth
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => onlyNumbersOnChange('markOut', e)}
              value={formik.values.markOut}
              id="cyp-markers-out-input-field"
              label="(Press O)"
              variant="outlined"
            />
            <Button
              id="cyp-markers-in-output-btn"
              onClick={fetchTimecode('markOut')}
              classes={{ root: classes.inAndOutButton }}
              variant="contained"
              color="secondary"
            >
              <Typography>OUT</Typography>
            </Button>
          </div>
          <TextField
            {...formik.getFieldProps('title')}
            onFocus={setGlobalStateMode(INSERT_MODE)}
            onBlur={setGlobalStateMode(VISUAL_MODE)}
            fullWidth
            id="cyp-markesr-title-input"
            label="Name"
            variant="outlined"
          />

          <TextField
            {...formik.getFieldProps('description')}
            onFocus={setGlobalStateMode(INSERT_MODE)}
            onBlur={setGlobalStateMode(VISUAL_MODE)}
            id="Notes"
            label="Notes"
            variant="outlined"
            rows="4"
            fullWidth
            multiline
          />
          <div className={classes.markersButtons}>
            <Button id="cyp-create-marker-btn" type="submit" variant="contained" color="primary">
              <Typography>{editMode ? 'UPDATE MARKER' : 'ADD MARKER'}</Typography>
            </Button>
            <div>
              <Button onClick={onCancelClick} color="secondary">
                <Typography>CANCEL</Typography>
              </Button>
              {editMode && (
                <Button onClick={buttonDeleteMarker} variant="outlined" className={classes.markersRemoveButton}>
                  <Typography>DELETE MARKER</Typography>
                </Button>
              )}
            </div>
          </div>
        </div>
      </form>
      <Typography component="h2" className={classes.markersListTitle}>
        Markers List
      </Typography>
      <MarkersList
        selectedItem={selectedMarker}
        df={assetTimecodeMetadata?.assetTimecodeMetadata?.dropframe || false}
        markers={markersList?.allMarkers?.edges || []}
        onClickItem={onClickItem}
        editMarkerClickHandler={editMarkerClickHandler}
        deleteMarkerClickHandler={deleteMarkerClickHandler}
        downloadMarkerClickHandler={downloadMarkerClickHandler}
        isAwsMedia={isAwsMedia}
      />
      {viewState.downloadMarkerDialog.markerId && <DownloadMarkerModal selectedMarkerId={selectedMarker ?? ''} />}
    </React.Fragment>
  );
};

export default MarkersTab;
